import { Injectable } from '@angular/core';
import {
  FillInfoProcessInput,
  FillInfoConfig,
  FillInfoConfigV1,
  FillInfoConfigV2,
  FillInfoTransform,
  ALLOWED_PROFILE_FIELDS,
  AllowedProfileField,
} from './fill-info.models';
import { DynamicService } from '../base/dynamic.service';
import { UserService } from '../user.service';
import { User } from '../../models/user.model';
import { Answer } from '../../models/questionnaire.model';

/**
 * Anchor global para filtrar logs do fillInfo no console
 * Use: localStorage.setItem('DEBUG_FILLINFO', 'true') para ativar
 */
const FILLINFO_ANCHOR = '[FILLINFO]';

@Injectable({
  providedIn: 'root',
})
export class FillInfoService {
  private debugEnabled = false;

  constructor(
    private dynamicService: DynamicService<any>,
    private userService: UserService
  ) {
    // Logs verbosos de info/warn só quando explicitamente habilitado via
    // localStorage.setItem('DEBUG_FILLINFO', 'true'). Erros sempre logam.
    this.debugEnabled = localStorage.getItem('DEBUG_FILLINFO') === 'true';
  }

  /**
   * Logger estruturado com breadcrumbs
   */
  private log(level: 'info' | 'warn' | 'error', anchor: string, data: any) {
    // Erros sempre logam; info/warn apenas em modo debug.
    if (level !== 'error' && !this.debugEnabled) {
      return;
    }

    const timestamp = new Date().toISOString();
    const prefix = `${FILLINFO_ANCHOR} [${level.toUpperCase()}] ${anchor}`;

    if (level === 'error') {
      console.error(prefix, { timestamp, ...data });
    } else if (level === 'warn') {
      console.warn(prefix, { timestamp, ...data });
    } else {
      console.log(prefix, { timestamp, ...data });
    }
  }

  /**
   * Processa fillInfo de uma questão respondida
   * Suporta V1 (legado) e V2 (seguro)
   */
  async process(input: FillInfoProcessInput): Promise<void> {
    const { config, answer, user } = input;

    this.log('info', 'PROCESS_START', {
      breadcrumb: '🚀 INÍCIO',
      answerId: (answer as any).id,
      userId: user.id,
      configVersion: this.detectVersion(config),
    });

    // Extrair valor da resposta
    const value = this.extractValue(answer, config.valueSource);
    if (!value) {
      this.log('warn', 'PROCESS_SKIPPED_NO_VALUE', {
        breadcrumb: '⏭️ PULADO - sem valor',
        answerId: (answer as any).id,
        valueSource: config.valueSource,
      });
      return;
    }

    this.log('info', 'VALUE_EXTRACTED', {
      breadcrumb: '📦 Valor extraído',
      value: typeof value === 'object' ? JSON.stringify(value) : value,
      valueType: typeof value,
      valueSource: config.valueSource || 'value',
    });

    // Dispatcher por versão
    const version = this.detectVersion(config);

    this.log('info', 'VERSION_DETECTED', {
      breadcrumb: `🔀 Versão detectada: V${version}`,
      version,
      isLegacy: version === 1,
    });

    if (version === 2) {
      await this.handleV2(config as FillInfoConfigV2, value, user);
    } else {
      await this.handleV1(config as FillInfoConfigV1, value, user);
    }

    this.log('info', 'PROCESS_COMPLETE', {
      breadcrumb: '✅ FINALIZADO',
      answerId: (answer as any).id,
      version,
    });
  }

  /**
   * Detecta versão da configuração
   */
  private detectVersion(config: FillInfoConfig): 1 | 2 {
    return (config as FillInfoConfigV2).version === 2 ? 2 : 1;
  }

  /**
   * Extrai valor da resposta baseado em valueSource
   */
  private extractValue(answer: Answer, valueSource?: string): any {
    const source = valueSource || 'value';

    // IMPORTANTE: manter compatibilidade com typo 'addtional_information'
    if (
      source === 'addtional_information' ||
      source === 'additional_information'
    ) {
      return (
        (answer as any).addtional_information ||
        (answer as any).additional_information
      );
    }

    return answer.value;
  }

  // ============================================================================
  // V1 - Handler Legado (MANTÉM COMPATIBILIDADE TOTAL)
  // ============================================================================

  /**
   * Handler V1 - Mantém lógica EXATA do código original
   * Usa DynamicService diretamente (bypass RLS como antes)
   */
  private async handleV1(
    config: FillInfoConfigV1,
    value: any,
    user: User
  ): Promise<void> {
    this.log('info', 'V1_HANDLER_START', {
      breadcrumb: '🔷 V1 - Legado (DynamicService)',
      table: config.from,
      updateField: config.update,
      updateType: config.updateType,
    });

    try {
      const { from, update, updateType, jsonKey, filters } = config;

      // Substituir $userId nos filtros (compatibilidade)
      const processedFilters = filters.map((f) => ({
        ...f,
        val: f.val === '$userId' ? user.id! : f.val,
      }));

      this.log('info', 'V1_FILTERS_PROCESSED', {
        breadcrumb: '🔍 Filtros processados',
        originalFilters: filters,
        processedFilters,
        userIdReplaced: filters.some((f) => f.val === '$userId'),
      });

      // Validação básica
      if (!from || !update || processedFilters.length === 0) {
        this.log('error', 'V1_INVALID_CONFIG', {
          breadcrumb: '❌ Configuração inválida',
          hasFrom: !!from,
          hasUpdate: !!update,
          hasFilters: processedFilters.length > 0,
        });
        return;
      }

      // Load da tabela no DynamicService (mantém comportamento original)
      await this.dynamicService.load(from);

      this.log('info', 'V1_DYNAMIC_SERVICE_LOADED', {
        breadcrumb: '📂 Tabela carregada no DynamicService',
        table: from,
      });

      // Chamar updatefillQuery (EXATAMENTE como antes)
      const result = await this.dynamicService.updatefillQuery(
        from,
        update,
        processedFilters,
        value,
        updateType || 'string',
        jsonKey
      );

      this.log('info', 'V1_UPDATE_SUCCESS', {
        breadcrumb: '✅ V1 - Atualização concluída',
        table: from,
        field: update,
        result,
      });
    } catch (error: any) {
      this.log('error', 'V1_UPDATE_FAILED', {
        breadcrumb: '❌ V1 - Falha na atualização',
        error: error.message,
        stack: error.stack,
      });
    }
  }

  // ============================================================================
  // V2 - Handler Seguro (RLS validado via /profile endpoint)
  // ============================================================================

  /**
   * Handler V2 - Usa endpoints seguros com RLS
   */
  private async handleV2(
    config: FillInfoConfigV2,
    value: any,
    user: User
  ): Promise<void> {
    this.log('info', 'V2_HANDLER_START', {
      breadcrumb: '🔶 V2 - Seguro (RLS validado)',
      target: config.target,
      field: config.field,
      transform: config.transform,
    });

    try {
      // Validar configuração V2
      const validation = this.validateV2Config(config);
      if (!validation.valid) {
        this.log('error', 'V2_INVALID_CONFIG', {
          breadcrumb: '❌ Configuração V2 inválida',
          errors: validation.errors,
        });
        return;
      }

      this.log('info', 'V2_CONFIG_VALID', {
        breadcrumb: '✅ Configuração validada',
        target: config.target,
      });

      // Dispatcher por target
      if (config.target === 'profile') {
        await this.handleProfileTarget(config, value, user);
      } else {
        this.log('error', 'V2_UNKNOWN_TARGET', {
          breadcrumb: '❌ Target desconhecido',
          target: config.target,
          supportedTargets: ['profile'],
        });
      }
    } catch (error: any) {
      this.log('error', 'V2_HANDLER_FAILED', {
        breadcrumb: '❌ V2 - Falha no handler',
        error: error.message,
        stack: error.stack,
      });
    }
  }

  /**
   * Valida configuração V2
   */
  private validateV2Config(config: FillInfoConfigV2): {
    valid: boolean;
    errors?: string[];
  } {
    const errors: string[] = [];

    if (!config.target) {
      errors.push('Missing target');
    }

    if (!config.field) {
      errors.push('Missing field');
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  }

  // ============================================================================
  // V2 - Profile Target Handler
  // ============================================================================

  /**
   * Handler para target 'profile'
   * Usa UserService.updateUser() que passa pelo endpoint /profile (RLS validado)
   */
  private async handleProfileTarget(
    config: FillInfoConfigV2,
    value: any,
    user: User
  ): Promise<void> {
    this.log('info', 'PROFILE_TARGET_START', {
      breadcrumb: '👤 Target: Profile',
      field: config.field,
      userId: user.id,
    });

    const { field, transform } = config;

    // Detectar se é campo aninhado (ex: "contacts.phone")
    const isNestedField = field.includes('.');

    this.log('info', 'FIELD_TYPE_DETECTED', {
      breadcrumb: isNestedField ? '🌲 Campo aninhado' : '📄 Campo simples',
      field,
      isNested: isNestedField,
    });

    if (isNestedField) {
      await this.handleNestedField(field, value, transform, user);
    } else {
      // Validar que é campo permitido antes de chamar handleFlatField
      if (!this.isAllowedProfileField(field)) {
        this.log('error', 'PROFILE_FIELD_FORBIDDEN', {
          breadcrumb: '🚫 Campo não permitido',
          field,
          allowed: ALLOWED_PROFILE_FIELDS,
        });
        return;
      }

      this.log('info', 'PROFILE_FIELD_ALLOWED', {
        breadcrumb: '✅ Campo permitido',
        field,
      });

      await this.handleFlatField(
        field as AllowedProfileField,
        value,
        transform,
        user
      );
    }
  }

  /**
   * Handler para campos simples (não aninhados)
   */
  private async handleFlatField(
    field: AllowedProfileField,
    value: any,
    transform: FillInfoTransform | undefined,
    user: User
  ): Promise<void> {
    this.log('info', 'FLAT_FIELD_PROCESSING', {
      breadcrumb: '📝 Processando campo simples',
      field,
      originalValue: value,
      transform: transform || 'none',
    });

    // Aplicar transformações
    const transformedValue = this.applyTransform(value, transform);

    if (transform) {
      this.log('info', 'TRANSFORM_APPLIED', {
        breadcrumb: `🔄 Transformação: ${transform}`,
        originalValue: value,
        transformedValue,
        transform,
      });
    }

    // Construir payload para /profile endpoint
    const payload: any = { [field]: transformedValue };

    this.log('info', 'FLAT_FIELD_SENDING', {
      breadcrumb: '📤 Enviando para /profile',
      payload,
      endpoint: 'POST /profile',
      note: 'Backend sincronizará display_name se necessário',
    });

    try {
      // Apenas atualizar PII - backend cuida da sincronização.
      // Enviar o id do usuário-alvo (cliente) para que o endpoint /profile
      // resolva o targetUser correto. Sem isso, o backend usa o usuário
      // autenticado (o AGENT em fluxo "em nome de"), gravando no PII errado.
      await this.userService.updateUser({ id: user.id }, payload);

      this.log('info', 'FLAT_FIELD_SUCCESS', {
        breadcrumb: '✅ Campo atualizado com sucesso',
        field,
        userId: user.id,
        value: transformedValue,
      });
    } catch (error: any) {
      this.log('error', 'FLAT_FIELD_FAILED', {
        breadcrumb: '❌ Falha ao atualizar campo',
        field,
        error: error.message,
        stack: error.stack,
      });
    }
  }

  /**
   * Handler para campos aninhados (ex: "contacts.phone")
   */
  private async handleNestedField(
    field: string,
    value: any,
    transform: FillInfoTransform | undefined,
    user: User
  ): Promise<void> {
    this.log('info', 'NESTED_FIELD_PROCESSING', {
      breadcrumb: '🌳 Processando campo aninhado',
      field,
      originalValue: value,
    });

    const parts = field.split('.');

    if (parts.length !== 2) {
      this.log('error', 'NESTED_FIELD_INVALID_FORMAT', {
        breadcrumb: '❌ Formato inválido de campo aninhado',
        field,
        parts,
        expected: 'parent.child',
        got: parts.length + ' parts',
      });
      return;
    }

    const [parentField, childKey] = parts;

    this.log('info', 'NESTED_FIELD_PARSED', {
      breadcrumb: '🔍 Campo aninhado parseado',
      originalField: field,
      parent: parentField,
      child: childKey,
    });

    // Validar que parent é permitido
    if (!this.isAllowedProfileField(parentField)) {
      this.log('error', 'NESTED_PARENT_FORBIDDEN', {
        breadcrumb: '🚫 Campo pai não permitido',
        parentField,
        allowed: ALLOWED_PROFILE_FIELDS,
      });
      return;
    }

    this.log('info', 'NESTED_PARENT_ALLOWED', {
      breadcrumb: '✅ Campo pai permitido',
      parentField,
    });

    // Aplicar transformações
    const transformedValue = this.applyTransform(value, transform);

    if (transform) {
      this.log('info', 'NESTED_TRANSFORM_APPLIED', {
        breadcrumb: `🔄 Transformação: ${transform}`,
        originalValue: value,
        transformedValue,
      });
    }

    // Construir payload aninhado
    const payload: any = {
      [parentField]: {
        [childKey]: transformedValue,
      },
    };

    this.log('info', 'NESTED_FIELD_SENDING', {
      breadcrumb: '📤 Enviando payload aninhado para /profile',
      payload,
      note: 'Backend fará merge JSONB preservando outros campos',
    });

    try {
      // Enviar o id do usuário-alvo (cliente) — mesmo motivo do campo simples.
      await this.userService.updateUser({ id: user.id }, payload);

      this.log('info', 'NESTED_FIELD_SUCCESS', {
        breadcrumb: '✅ Campo aninhado atualizado com sucesso',
        field,
        parentField,
        childKey,
        userId: user.id,
        value: transformedValue,
      });
    } catch (error: any) {
      this.log('error', 'NESTED_FIELD_FAILED', {
        breadcrumb: '❌ Falha ao atualizar campo aninhado',
        field,
        error: error.message,
        stack: error.stack,
      });
    }
  }

  /**
   * Valida se campo é permitido para profile
   */
  private isAllowedProfileField(field: string): field is AllowedProfileField {
    return ALLOWED_PROFILE_FIELDS.includes(field as any);
  }

  /**
   * Aplica transformações no valor
   */
  private applyTransform(value: any, transform?: FillInfoTransform): any {
    if (!transform || typeof value !== 'string') {
      return value;
    }

    switch (transform) {
      case 'uppercase':
        return value.toUpperCase();
      case 'lowercase':
        return value.toLowerCase();
      case 'trim':
        return value.trim();
      case 'capitalize':
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      default:
        return value;
    }
  }
}
